/**
 * Memory monitoring utility for debugging memory leaks
 *
 * Usage:
 * 1. Import and start monitoring in your app
 * 2. Check browser console for periodic memory reports
 * 3. Use memoryMonitor.getReport() for on-demand stats
 */

import './profiling.types';

interface MemoryStats {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
  usedMB: number;
  totalMB: number;
  limitMB: number;
  percentUsed: number;
  timestamp: Date;
}

interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

declare global {
  interface Performance {
    memory?: PerformanceMemory;
  }
}

class MemoryMonitor {
  private intervalId: number | null = null;
  private history: MemoryStats[] = [];
  private maxHistorySize = 100;

  /**
   * Start monitoring memory usage
   * @param intervalMs How often to check memory (default: 30 seconds)
   * @param logToConsole Whether to log to console (default: true)
   */
  start(intervalMs = 30000, logToConsole = true): void {
    // Skip if not in browser environment
    if (typeof window === 'undefined') {
      return;
    }

    if (this.intervalId) {
      console.warn('[MemoryMonitor] Already running');
      return;
    }

    if (!this.isMemoryAPIAvailable()) {
      console.warn('[MemoryMonitor] Performance.memory API not available');
      return;
    }

    console.log(
      `[MemoryMonitor] Started monitoring (interval: ${intervalMs}ms)`,
    );

    this.intervalId = window.setInterval(() => {
      const stats = this.getStats();
      if (!stats) return;

      this.history.push(stats);

      // Keep history bounded
      if (this.history.length > this.maxHistorySize) {
        this.history.shift();
      }

      if (logToConsole) {
        this.logStats(stats);
      }

      // Alert if memory usage is high
      if (stats.percentUsed > 90) {
        console.error(
          `[MemoryMonitor] 🔴 CRITICAL: Memory usage at ${stats.percentUsed.toFixed(1)}%`,
        );
      } else if (stats.percentUsed > 75) {
        console.warn(
          `[MemoryMonitor] ⚠️ WARNING: Memory usage at ${stats.percentUsed.toFixed(1)}%`,
        );
      }
    }, intervalMs);

    // Log initial stats
    const initialStats = this.getStats();
    if (initialStats && logToConsole) {
      this.logStats(initialStats);
    }
  }

  /**
   * Stop monitoring
   */
  stop(): void {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('[MemoryMonitor] Stopped monitoring');
    }
  }

  /**
   * Get current memory statistics
   */
  getStats(): MemoryStats | null {
    if (!this.isMemoryAPIAvailable()) {
      return null;
    }

    const memory = performance.memory as PerformanceMemory;

    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      usedMB: this.bytesToMB(memory.usedJSHeapSize),
      totalMB: this.bytesToMB(memory.totalJSHeapSize),
      limitMB: this.bytesToMB(memory.jsHeapSizeLimit),
      percentUsed: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
      timestamp: new Date(),
    };
  }

  /**
   * Get memory usage report
   */
  getReport(): string {
    const current = this.getStats();
    if (!current) {
      return 'Memory API not available';
    }

    const trend = this.calculateTrend();

    return `
╔════════════════════════════════════════════════════════════╗
║                   MEMORY USAGE REPORT                      ║
╠════════════════════════════════════════════════════════════╣
║ Used Memory:    ${current.usedMB.toFixed(2)} MB (${current.percentUsed.toFixed(1)}%)
║ Total Heap:     ${current.totalMB.toFixed(2)} MB
║ Heap Limit:     ${current.limitMB.toFixed(2)} MB
║ Trend (5 min):  ${trend > 0 ? '📈' : trend < 0 ? '📉' : '➡️'} ${Math.abs(trend).toFixed(2)} MB
║ Samples:        ${this.history.length}
║ Timestamp:      ${current.timestamp.toLocaleTimeString()}
╚════════════════════════════════════════════════════════════╝`;
  }

  /**
   * Get memory history
   */
  getHistory(): MemoryStats[] {
    return [...this.history];
  }

  /**
   * Clear history
   */
  clearHistory(): void {
    this.history = [];
  }

  /**
   * Export history as CSV
   */
  exportHistoryCSV(): string {
    const headers = 'Timestamp,Used MB,Total MB,Limit MB,Percent Used\n';
    const rows = this.history
      .map(
        (stat) =>
          `${stat.timestamp.toISOString()},${stat.usedMB},${stat.totalMB},${stat.limitMB},${stat.percentUsed}`,
      )
      .join('\n');
    return headers + rows;
  }

  /**
   * Trigger garbage collection (only works if --expose-gc flag is set)
   */
  forceGC(): void {
    if (typeof gc === 'function') {
      gc();
      console.log('[MemoryMonitor] Forced garbage collection');
    } else {
      console.warn(
        '[MemoryMonitor] GC not exposed. Start app with --expose-gc flag',
      );
    }
  }

  // Private helpers

  private isMemoryAPIAvailable(): boolean {
    return typeof performance !== 'undefined' && !!performance.memory;
  }

  private bytesToMB(bytes: number): number {
    return bytes / (1024 * 1024);
  }

  private logStats(stats: MemoryStats): void {
    const icon = stats.percentUsed > 75 ? '⚠️' : '✅';
    console.log(
      `${icon} [MemoryMonitor] ${stats.usedMB.toFixed(2)} MB / ${stats.limitMB.toFixed(2)} MB (${stats.percentUsed.toFixed(1)}%)`,
    );
  }

  private calculateTrend(): number {
    if (this.history.length < 2) return 0;

    // Calculate trend over last 10 samples (or all if less)
    const samples = Math.min(10, this.history.length);
    const recent = this.history.slice(-samples);
    const first = recent[0];
    const last = recent[recent.length - 1];

    return last.usedMB - first.usedMB;
  }
}

// Singleton instance
export const memoryMonitor = new MemoryMonitor();

// Auto-start in development (browser only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  // Start monitoring 10 seconds after app loads
  setTimeout(() => {
    memoryMonitor.start(30000); // Check every 30 seconds
  }, 10000);
}

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.memoryMonitor = memoryMonitor;
}
